import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Search, Trophy, UserCheck, UserX, FolderOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const ContributorAnalytics = () => {
  const { user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('total_projects');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceProjects, setResourceProjects] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Admin search
  const [adminSearch, setAdminSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    try {
      const res = await api.get('/contributor-analytics');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.contributors;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c => c.resource.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      const av = a[sortKey] || 0;
      const bv = b[sortKey] || 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return list;
  }, [data, searchQuery, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null;
    return sortDir === 'desc' ? <ChevronDown className="w-3 h-3 inline ml-1" /> : <ChevronUp className="w-3 h-3 inline ml-1" />;
  };

  const openDetail = async (resource) => {
    setSelectedResource(resource);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/contributor-analytics/${encodeURIComponent(resource)}`);
      setResourceProjects(res.data);
    } catch (error) {
      toast.error('Failed to load details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleAdminSearch = async () => {
    if (adminSearch.trim().length < 2) { toast.error('Type at least 2 characters'); return; }
    setSearching(true);
    try {
      const res = await api.get(`/admin/project-search?q=${encodeURIComponent(adminSearch)}`);
      setSearchResults(res.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const summary = data?.summary || {};

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="contributor-analytics-title">Contributor Analytics</h1>
          <p className="text-muted-foreground">Resource performance and project assignments</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="pt-5 text-center">
              <p className="text-sm text-muted-foreground mb-1">Total Resources</p>
              <p className="text-3xl font-bold text-violet-600">{summary.total_resources}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-5 text-center">
              <p className="text-sm text-muted-foreground mb-1">Active</p>
              <p className="text-3xl font-bold text-green-600">{summary.active_resources}</p>
              <p className="text-xs text-muted-foreground">with in-progress work</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-400">
            <CardContent className="pt-5 text-center">
              <p className="text-sm text-muted-foreground mb-1">Inactive</p>
              <p className="text-3xl font-bold text-gray-500">{summary.inactive_resources}</p>
              <p className="text-xs text-muted-foreground">all projects completed</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-5 text-center">
              <p className="text-sm text-muted-foreground mb-1">Total Projects</p>
              <p className="text-3xl font-bold text-blue-600">{summary.total_projects}</p>
            </CardContent>
          </Card>
        </div>

        {/* Project Search (Admin + Owner) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4" />
              Project Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={adminSearch}
                onChange={(e) => setAdminSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdminSearch()}
                placeholder="Search by title, PO number, episode, client, resource..."
                data-testid="admin-project-search"
              />
              <Button onClick={handleAdminSearch} disabled={searching}>
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>
            {searchResults && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Found {searchResults.projects.length} projects, {searchResults.billings.length} billing records
                </p>
                {searchResults.projects.length > 0 && (
                  <div className="overflow-x-auto max-h-72 border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">PO Number</TableHead>
                          <TableHead className="whitespace-nowrap">Job ID</TableHead>
                          <TableHead className="whitespace-nowrap">Main Title</TableHead>
                          <TableHead className="whitespace-nowrap">Episode</TableHead>
                          <TableHead className="whitespace-nowrap">Client</TableHead>
                          <TableHead className="whitespace-nowrap">Task</TableHead>
                          <TableHead className="whitespace-nowrap">Language</TableHead>
                          <TableHead className="whitespace-nowrap">Duration</TableHead>
                          <TableHead className="whitespace-nowrap">Resource</TableHead>
                          <TableHead className="whitespace-nowrap">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.projects.slice(0, 15).map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs whitespace-nowrap">{p.po_number || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{p.job_id || '—'}</TableCell>
                            <TableCell className="text-xs">{p.main_title || '—'}</TableCell>
                            <TableCell className="text-xs">{p.episode_title || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{p.client || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{p.task || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{p.language || '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{p.qty > 0 ? p.qty : '—'}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{p.resource || '—'}</TableCell>
                            <TableCell className="text-xs">
                              {p.task_status === 'completed'
                                ? <Badge className="bg-green-600 text-white text-[10px]">Done</Badge>
                                : <Badge className="bg-blue-500 text-white text-[10px]">Active</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contributors Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                All Contributors ({filtered.length})
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter by name/email..."
                  className="pl-10 h-8"
                  data-testid="contributor-filter"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => toggleSort('total_projects')}>
                      Projects <SortIcon col="total_projects" />
                    </TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => toggleSort('completed')}>
                      Completed <SortIcon col="completed" />
                    </TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => toggleSort('in_progress')}>
                      In Progress <SortIcon col="in_progress" />
                    </TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => toggleSort('total_qty')}>
                      Total QTY <SortIcon col="total_qty" />
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Clients</TableHead>
                    <TableHead className="whitespace-nowrap">Tasks</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c, idx) => (
                    <TableRow key={c.resource} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(c.resource)}>
                      <TableCell className="text-center text-muted-foreground text-xs">{idx + 1}</TableCell>
                      <TableCell className="font-medium text-sm whitespace-nowrap">
                        {idx < 3 && c.resource !== 'Unassigned' && (
                          <span className="mr-1">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                        )}
                        {c.resource}
                      </TableCell>
                      <TableCell className="font-semibold">{c.total_projects}</TableCell>
                      <TableCell className="text-green-600">{c.completed}</TableCell>
                      <TableCell className="text-blue-600">{c.in_progress}</TableCell>
                      <TableCell className="whitespace-nowrap">{c.total_qty}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{c.unique_clients} clients</TableCell>
                      <TableCell className="text-xs">
                        <div className="flex gap-1 flex-wrap max-w-[150px]">
                          {c.tasks.slice(0, 3).map(t => (
                            <Badge key={t} variant="outline" className="text-[10px] px-1">{t}</Badge>
                          ))}
                          {c.tasks.length > 3 && <span className="text-[10px] text-muted-foreground">+{c.tasks.length - 3}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.in_progress > 0
                          ? <Badge className="bg-green-100 text-green-700 text-[10px]"><UserCheck className="w-3 h-3 mr-1" />Active</Badge>
                          : <Badge className="bg-gray-100 text-gray-500 text-[10px]"><UserX className="w-3 h-3 mr-1" />Inactive</Badge>}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 text-xs">View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resource Detail Dialog */}
      <Dialog open={!!selectedResource} onOpenChange={(open) => { if (!open) setSelectedResource(null); }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Projects for: {selectedResource}
            </DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{resourceProjects.length} projects found</p>
              <div className="overflow-x-auto max-h-[50vh] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">PO Number</TableHead>
                      <TableHead className="whitespace-nowrap">Main Title</TableHead>
                      <TableHead className="whitespace-nowrap">Episode</TableHead>
                      <TableHead className="whitespace-nowrap">Client</TableHead>
                      <TableHead className="whitespace-nowrap">Task</TableHead>
                      <TableHead className="whitespace-nowrap">Language</TableHead>
                      <TableHead className="whitespace-nowrap">QTY</TableHead>
                      <TableHead className="whitespace-nowrap">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resourceProjects.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs whitespace-nowrap">{p.po_number || '—'}</TableCell>
                        <TableCell className="text-xs">{p.main_title || '—'}</TableCell>
                        <TableCell className="text-xs">{p.episode_title || '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{p.client || '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{p.task || '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{p.language || '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{p.qty > 0 ? p.qty : '—'}</TableCell>
                        <TableCell className="text-xs">
                          {p.task_status === 'completed'
                            ? <Badge className="bg-green-600 text-white text-[10px]">Done</Badge>
                            : <Badge className="bg-blue-500 text-white text-[10px]">Active</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ContributorAnalytics;
